import { useState, useEffect, useCallback, useRef } from "react";
import { useCurrentUser } from "./use-current-user";
import { useContractorOrganizations } from "./use-contractor-organizations";
import { useOrganizationStore } from "@/stores/organizationStore";

const SELECTED_ORG_STORAGE_KEY = "selectedOrganizationId";

/**
 * Hook to manage organization selection for contractors
 * - Contractors can select between multiple organizations
 * - Employees see their single organization (read-only)
 * - Selection is persisted in localStorage
 */
export function useOrganizationSelector() {
  const { user } = useCurrentUser();
  const { organizations, hasMultipleOrgs } = useContractorOrganizations();
  const userId = (user as any)?.id || null;
  
  const userOrgId = (user as any)?.organizationId || null;
  const userOrgName = (user as any)?.organizationName || null;
  const { selectedOrganizationId, selectedOrganizationName, setOrganization, clearOrganization } = useOrganizationStore();
  const previousUserIdRef = useRef<string | null>(null);
  
  // Determine default org: stored -> user's org -> first returned
  const defaultOrgId = userOrgId || organizations[0]?.id || null;
  
  // State for selected organization (for contractors)
  const [selectedOrgId, setSelectedOrgIdState] = useState<string | null>(selectedOrganizationId || defaultOrgId);

  // Update selected org when default changes
  useEffect(() => {
    const effectiveId = selectedOrganizationId || defaultOrgId;
    if (effectiveId && effectiveId !== selectedOrgId) {
      setSelectedOrgIdState(effectiveId);
    }
  }, [defaultOrgId, selectedOrganizationId, selectedOrgId]);

  // Save to localStorage when selection changes
  const setSelectedOrgId = useCallback((orgId: string | null) => {
    const orgName = organizations.find(o => o.id === orgId)?.name || null;
    setSelectedOrgIdState(orgId);
    if (orgId) setOrganization(orgId, orgName); else clearOrganization();
    if (typeof window !== 'undefined') {
      if (orgId) {
        localStorage.setItem(SELECTED_ORG_STORAGE_KEY, orgId);
      } else {
        localStorage.removeItem(SELECTED_ORG_STORAGE_KEY);
      }
    }
  }, [organizations, setOrganization, clearOrganization]);

  const setSelectedOrgIdAndReset = useCallback((orgId: string | null) => {
    // Avoid unnecessary reload when org doesn't change
    if (!orgId || orgId === selectedOrgId) {
      return;
    }

    setSelectedOrgId(orgId);

    // Hard reset app state to prevent cross-org stale data leakage
    if (typeof window !== 'undefined') {
      window.location.assign('/dashboard');
    }
  }, [selectedOrgId, setSelectedOrgId]);

  // When the authenticated user changes, reset the cached selection to avoid leaking the previous state
  useEffect(() => {
    const previousUserId = previousUserIdRef.current;

    if (!userId) {
      if (previousUserId !== null) {
        setSelectedOrgId(null);
      }
      previousUserIdRef.current = null;
      return;
    }

    if (previousUserId && previousUserId !== userId) {
      setSelectedOrgId(null);
    }

    previousUserIdRef.current = userId;
  }, [userId, setSelectedOrgId]);

  // Ensure the selected organization still belongs to the current user
  useEffect(() => {
    if (!selectedOrgId || !userId) return;
    const hasAccess = organizations.some((org: any) => org.id === selectedOrgId);
    if (!hasAccess && organizations.length > 0) {
      const fallbackOrgId = userOrgId || organizations[0]?.id || null;
      if (fallbackOrgId && fallbackOrgId !== selectedOrgId) {
        setSelectedOrgId(fallbackOrgId);
      }
    }
  }, [selectedOrgId, organizations, userOrgId, setSelectedOrgId, userId]);

  // For contractors: use selected org, for employees: use their organization
  const activeOrganizationId = hasMultipleOrgs ? selectedOrgId : (userOrgId || organizations[0]?.id || null);
  
  // Find organization name and relation (role) for the active organization
  const activeOrg = activeOrganizationId 
    ? organizations.find((org: any) => org.id === activeOrganizationId)
    : null;
  
  const activeOrganizationName =
    activeOrg?.name ||
    (hasMultipleOrgs && selectedOrgId 
      ? organizations.find((org: any) => org.id === selectedOrgId)?.name
      : null) ||
    userOrgName || selectedOrganizationName ||
    organizations[0]?.name ||
    null;
  
  // Role from org-scoped channels: contractor_role for contractor orgs, resolvedOrgRole/orgRole for member orgs
  const activeOrgRole =
    activeOrg?.contractor_role ??
    (activeOrg?.relation === 'owner' ? 'owner' : null) ??
    (user as any)?.resolvedOrgRole ??
    (user as any)?.orgRole ??
    null;
  const isContractorInActiveOrg = ['contractor', 'watcher', 'business_owner'].includes(activeOrgRole || '');

  return {
    // Current active organization (selected for contractors, default for employees)
    activeOrganizationId,
    activeOrganizationName,
    
    // Role in the active organization
    activeOrgRole,
    isContractor: isContractorInActiveOrg,
    
    // Selection state (only relevant for contractors)
    selectedOrgId,
    setSelectedOrgId,
    setSelectedOrgIdAndReset,
    
    // Metadata
    organizations,
    hasMultipleOrgs,
    canSelect: hasMultipleOrgs,
  };
}
